import { Show } from 'solid-js';
import { DropdownOption, FieldType, FormFieldContext } from '../forms-types';
import { errorDescribedBy, FieldErrorMessage, fieldErrorId, FormSelect, FormFieldLabel, FormSelectWrapper, IconWrapper } from './primitives';
import { ChevronDownCviIcon } from '../../icons-library/chevron-down-cvi';
import { countries, atosGlobalCountrySlugToCountryCode } from '../../../tools/countries-data';
import { parseFieldOptions } from '../utils';
import { getUserTypes } from '../constants';

type FieldProps = {
    field: FieldType;
    ctx: FormFieldContext;
};

// Custom WP dropdown: options come from extraSettings (newline-separated, optional pipe-separated label/value).
export const Dropdown = (props: FieldProps) => {
    const { formId, formValues, errors, onDropdownChange } = props.ctx;

    const extraSettings = () => props.field.extraSettings;
    const required = () => props.field.required;
    const eloquaName = () => extraSettings()?.name.value ?? '';
    // Backwards compability for when the dropdown options were coming from info.value (different WP setup).
    const optionsString = () => extraSettings()?.options?.value || extraSettings()?.info.value.split(',').join('\n') || '';
    const formatedOptions = (): DropdownOption[] => parseFieldOptions(optionsString());
    const placeholderValue = () => extraSettings()?.placeholder.value ?? '';
    const placeholderText = () => `${placeholderValue()}${required() ? '*' : ''}`;
    const selectId = () => `${formId}-custom-dropdown-select-${eloquaName()}`;
    const labelId = () => `${formId}-custom-dropdown-select-label-${eloquaName()}`;

    return (
        <Show when={extraSettings()}>
            <div>
                <FormFieldLabel for={selectId()} id={labelId()}>
                    {placeholderText()}
                </FormFieldLabel>
                <FormSelectWrapper>
                    <FormSelect
                        id={selectId()}
                        aria-describedby={errorDescribedBy(selectId(), !!errors()[eloquaName()])}
                        required={required()}
                        value={formValues()[eloquaName()]}
                        hasErrors={!!errors()[eloquaName()]}
                        onChange={(e) => onDropdownChange(eloquaName(), e.currentTarget.value)}
                        name={'custom-dropdown-' + eloquaName()}
                        aria-labelledby={labelId()}
                        title={placeholderText()}
                    >
                        <option value="">{placeholderText().replace('*', '')}</option>
                        {formatedOptions().map((option) => (
                            <option value={option.value}>{option.text}</option>
                        ))}
                    </FormSelect>

                    <IconWrapper>
                        <ChevronDownCviIcon size="default" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>

                <Show when={errors()[eloquaName()]}>
                    <FieldErrorMessage errors={errors} fieldKey={eloquaName()} id={fieldErrorId(selectId())} />
                </Show>
            </div>
        </Show>
    );
};

export const CountrySelect = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize, siteInfo } = props.ctx;

    const slug = () => props.field.slug;
    const title = () => `${localize('country', 'Country')}${props.field.required ? '*' : ''}`;
    const options = (): DropdownOption[] => countries.map((country) => ({ value: country.code, text: country.name }));
    const currentSelectCountryCode = () => atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode];
    const selectId = () => `${formId}-country-select`;
    const labelId = () => `${formId}-country-select-label`;

    return (
        <div>
            <FormFieldLabel for={selectId()} id={labelId()}>
                {title()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id={selectId()}
                    aria-describedby={errorDescribedBy(selectId(), !!errors()[slug()])}
                    name={slug()}
                    required={props.field.required}
                    value={formValues().country?.code}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby={labelId()}
                    title={title()}
                    autocomplete="country"
                >
                    <option value="">{title().replace('*', '')}</option>
                    {options().map((option) => (
                        <option selected={option.value === currentSelectCountryCode()} value={option.value}>{option.text}</option>
                    ))}
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} id={fieldErrorId(selectId())} />
            </Show>
        </div>
    );
};

export const UserTypeSelect = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const options = () => getUserTypes(localize);
    const placeholder = () => `${localize('user-type', 'User type')}${props.field.required ? '*' : ''}`;
    const selectId = () => `${formId}-user-type-select`;
    const labelId = () => `${formId}-user-type-select-label`;

    return (
        <div>
            <FormFieldLabel for={selectId()} id={labelId()}>
                {placeholder()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id={selectId()}
                    aria-describedby={errorDescribedBy(selectId(), !!errors()[slug()])}
                    name={slug()}
                    required={props.field.required}
                    value={formValues()[slug()]}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby={labelId()}
                    title={placeholder()}
                >
                    <option value="">{placeholder().replace('*', '')}</option>
                    {options().map((option) => (
                        <option value={option.value}>{option.text}</option>
                    ))}
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} id={fieldErrorId(selectId())} />
            </Show>
        </div>
    );
};

export const CommunicationPreferenceSelect = (props: FieldProps) => {
    const { formId, formValues, errors, onInputChange, localize } = props.ctx;

    const slug = () => props.field.slug;
    const placeholder = () => `${localize('communication-preference', 'Communication preference')}${props.field.required ? '*' : ''}`;
    const selectId = () => `${formId}-communication-preference-select`;
    const labelId = () => `${formId}-communication-preference-select-label`;

    return (
        <div>
            <FormFieldLabel for={selectId()} id={labelId()}>
                {placeholder()}
            </FormFieldLabel>

            <FormSelectWrapper>
                <FormSelect
                    id={selectId()}
                    aria-describedby={errorDescribedBy(selectId(), !!errors()[slug()])}
                    name={slug()}
                    required={props.field.required}
                    value={formValues()[slug()]}
                    hasErrors={!!errors()[slug()]}
                    onChange={(e) => onInputChange(slug(), e.currentTarget.value)}
                    aria-labelledby={labelId()}
                    title={placeholder()}
                >
                    <option value="">{placeholder().replace('*', '')}</option>
                    <option value="Phone">{localize('phone', 'Phone')}</option>
                    <option value="Email">{localize('email', 'E-mail')}</option>
                </FormSelect>

                <IconWrapper>
                    <ChevronDownCviIcon size="default" ariaHidden={true} />
                </IconWrapper>
            </FormSelectWrapper>

            <Show when={errors()[slug()]}>
                <FieldErrorMessage errors={errors} fieldKey={slug()} id={fieldErrorId(selectId())} />
            </Show>
        </div>
    );
};
